import { useState, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { KiiDialogLayout } from "../kii/kii-dialog-layout";
import { Progress } from "../ui/progress";
import { useFileUpload, type ImportMode } from "../../hooks/useFileUpload";
import { useDocument } from "../../hooks/useDocuments";
import {
  Upload,
  X,
  Music,
  Image,
  FileText,
  CheckCircle,
  AlertCircle,
  Loader2,
  Clock,
} from "lucide-react";

function DocumentProcessingRow({ projectId, documentId }: { projectId: string; documentId: string }) {
  const { t } = useTranslation();
  const { data: doc } = useDocument(projectId, documentId);
  const status = doc?.status ?? "pending";

  let icon = <Clock className="h-4 w-4 text-muted-foreground shrink-0" />;
  let label = t("projectDetail.importDialog.statusQueued", "Queued");
  let labelClass = "text-muted-foreground";

  if (status === "pending") {
    icon = <Clock className="h-4 w-4 text-muted-foreground shrink-0" />;
    label = t("projectDetail.importDialog.statusPending", "Waiting to process");
  } else if (status === "processing") {
    icon = <Loader2 className="h-4 w-4 text-blue-500 shrink-0 animate-spin" />;
    label = t("projectDetail.importDialog.statusProcessing", "Transcribing & analyzing…");
    labelClass = "text-blue-600";
  } else if (status === "ready") {
    icon = <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />;
    label = t("projectDetail.importDialog.statusReady", "Ready");
    labelClass = "text-green-600";
  } else if (status === "failed") {
    icon = <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />;
    label = t("projectDetail.importDialog.statusFailed", "Processing failed");
    labelClass = "text-red-600";
  }

  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-card border border-border rounded-lg">
      <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
      <span className="text-sm text-foreground truncate flex-1">
        {doc?.name ?? documentId}
      </span>
      {icon}
      <span className={`text-xs shrink-0 ${labelClass}`}>{label}</span>
    </div>
  );
}

const ACCEPTED_EXTENSIONS = [
  ".m4a", ".mp3", ".wav", ".ogg", ".aac",
  ".jpg", ".jpeg", ".png", ".webp", ".heic",
  ".pdf", ".docx", ".md",
];

const ACCEPTED_MIME_TYPES = [
  "audio/mp4", "audio/mpeg", "audio/wav", "audio/ogg", "audio/aac",
  "audio/x-m4a", "audio/m4a",
  "image/jpeg", "image/png", "image/webp", "image/heic",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/markdown",
];

const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500 MB

const AUDIO_EXTENSIONS = [".m4a", ".mp3", ".wav", ".ogg", ".aac"];
const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".heic"];

function getFileExtension(name: string): string {
  const dotIndex = name.lastIndexOf(".");
  if (dotIndex === -1) return "";
  return name.slice(dotIndex).toLowerCase();
}

function isAudioFile(name: string): boolean {
  return AUDIO_EXTENSIONS.includes(getFileExtension(name));
}

function isImageFile(name: string): boolean {
  return IMAGE_EXTENSIONS.includes(getFileExtension(name));
}

function isMarkdownFile(name: string): boolean {
  return getFileExtension(name) === ".md";
}

function detectMode(files: File[]): ImportMode {
  if (files.length <= 1) return "multi-document";
  const hasAudioOrMd = files.some(
    (f) => isAudioFile(f.name) || isMarkdownFile(f.name)
  );
  const nonAudioMdFiles = files.filter(
    (f) => !isAudioFile(f.name) && !isMarkdownFile(f.name)
  );
  const allRemainingAreImages =
    nonAudioMdFiles.length === 0 ||
    nonAudioMdFiles.every((f) => isImageFile(f.name));
  return hasAudioOrMd && allRemainingAreImages ? "bundle" : "multi-document";
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(name: string) {
  if (isAudioFile(name)) return Music;
  if (isImageFile(name)) return Image;
  return FileText;
}

function validateFile(file: File): string | null {
  const ext = getFileExtension(file.name);
  const hasValidExt = ACCEPTED_EXTENSIONS.includes(ext);
  const hasValidMime =
    file.type && ACCEPTED_MIME_TYPES.some((m) => file.type.startsWith(m.split("/")[0]) || file.type === m);
  if (!hasValidExt && !hasValidMime) return "invalidType";
  if (file.size > MAX_FILE_SIZE) return "fileTooBig";
  return null;
}

interface ImportDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
}

export function ImportDocumentDialog({
  open,
  onOpenChange,
  projectId,
}: ImportDocumentDialogProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [documentName, setDocumentName] = useState("");
  const [validationErrors, setValidationErrors] = useState<Map<string, string>>(
    new Map()
  );
  const [isDragActive, setIsDragActive] = useState(false);

  const mode = useMemo(() => detectMode(files), [files]);

  const {
    startUpload,
    isUploading,
    fileProgress,
    error,
    isDone,
    documentsCreated,
    createdDocumentIds,
    reset,
  } = useFileUpload({ projectId });

  // Derive phase from hook state
  const phase: "select" | "uploading" | "done" = isDone
    ? "done"
    : isUploading
      ? "uploading"
      : "select";

  const addFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const fileArray = Array.from(newFiles);
      const errors = new Map(validationErrors);
      const validFiles: File[] = [];

      for (const file of fileArray) {
        const validationError = validateFile(file);
        if (validationError) {
          errors.set(file.name, validationError);
        } else {
          validFiles.push(file);
        }
      }

      setValidationErrors(errors);
      setFiles((prev) => {
        // Avoid duplicates by name
        const existingNames = new Set(prev.map((f) => f.name));
        const unique = validFiles.filter((f) => !existingNames.has(f.name));
        return [...prev, ...unique];
      });
    },
    [validationErrors]
  );

  const removeFile = useCallback(
    (filename: string) => {
      setFiles((prev) => prev.filter((f) => f.name !== filename));
      setValidationErrors((prev) => {
        const next = new Map(prev);
        next.delete(filename);
        return next;
      });
    },
    []
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragActive(false);
      if (e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles]
  );

  const handleBrowse = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        addFiles(e.target.files);
        // Reset input so the same file can be selected again
        e.target.value = "";
      }
    },
    [addFiles]
  );

  const handleImport = useCallback(() => {
    if (files.length === 0) return;
    startUpload(files, mode, mode === "bundle" ? documentName : undefined);
  }, [files, mode, documentName, startUpload]);

  const handleClose = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !isUploading) {
        reset();
        setFiles([]);
        setDocumentName("");
        setValidationErrors(new Map());
        setIsDragActive(false);
      }
      onOpenChange(newOpen);
    },
    [isUploading, reset, onOpenChange]
  );

  const canImport =
    files.length > 0 && (mode !== "bundle" || documentName.trim().length > 0);

  const uploadingFileCount = fileProgress.filter(
    (fp) => fp.status === "done"
  ).length;
  const totalFileCount = fileProgress.length;

  return (
    <KiiDialogLayout
      open={open}
      onOpenChange={handleClose}
      title={
        phase === "done"
          ? t("projectDetail.importDialog.uploadComplete")
          : t("projectDetail.importDialog.title")
      }
      size="lg"
      hasUnsavedChanges={isUploading}
      footer={
        phase === "select" ? (
          <>
            <button
              type="button"
              onClick={() => handleClose(false)}
              className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={handleImport}
              disabled={!canImport}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("projectDetail.importDialog.import")}
            </button>
          </>
        ) : phase === "done" ? (
          <button
            type="button"
            onClick={() => handleClose(false)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t("common.close")}
          </button>
        ) : undefined
      }
    >
      <div className="mt-4">
        {/* Phase 1: Select files */}
        {phase === "select" && (
          <div className="space-y-4">
            {/* Drop zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleBrowse}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-950"
                  : "border-border hover:border-gray-400 hover:bg-accent"
              }`}
            >
              <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {isDragActive
                  ? t("projectDetail.importDialog.dropzoneActive")
                  : t("projectDetail.importDialog.dropzoneText")}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                {t("projectDetail.importDialog.acceptedTypes")}
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              accept={ACCEPTED_EXTENSIONS.join(",")}
              onChange={handleFileInputChange}
            />

            {/* Validation errors for rejected files */}
            {validationErrors.size > 0 && (
              <div className="space-y-1">
                {Array.from(validationErrors.entries()).map(([name, errKey]) => (
                  <div
                    key={name}
                    className="flex items-center gap-2 text-sm text-red-600 bg-red-50 dark:bg-red-950 px-3 py-1.5 rounded"
                  >
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span className="truncate">{name}</span>
                    <span>- {t(`projectDetail.importDialog.${errKey}`)}</span>
                    <button
                      onClick={() =>
                        setValidationErrors((prev) => {
                          const next = new Map(prev);
                          next.delete(name);
                          return next;
                        })
                      }
                      className="ml-auto shrink-0"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* File list */}
            {files.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t("projectDetail.importDialog.fileCount", {
                    count: files.length,
                  })}
                </p>
                <div className="divide-y divide-border border border-border rounded-lg overflow-hidden">
                  {files.map((file) => {
                    const Icon = getFileIcon(file.name);
                    return (
                      <div
                        key={file.name}
                        className="flex items-center gap-3 px-3 py-2 bg-card"
                      >
                        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-sm text-foreground truncate flex-1">
                          {file.name}
                        </span>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {formatFileSize(file.size)}
                        </span>
                        <button
                          onClick={() => removeFile(file.name)}
                          className="p-1 text-muted-foreground hover:text-red-500 transition-colors shrink-0"
                          title={t("projectDetail.importDialog.removeFile")}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Mode indicator */}
                <p className="text-sm text-muted-foreground italic">
                  {mode === "bundle"
                    ? t("projectDetail.importDialog.bundleMode")
                    : t("projectDetail.importDialog.multiMode")}
                </p>

                {/* Document name input (bundle mode only) */}
                {mode === "bundle" && (
                  <div>
                    <label
                      htmlFor="importDocName"
                      className="block text-sm font-medium text-foreground mb-1"
                    >
                      {t("projectDetail.importDialog.documentName")}
                    </label>
                    <input
                      id="importDocName"
                      type="text"
                      value={documentName}
                      onChange={(e) => setDocumentName(e.target.value)}
                      placeholder={t(
                        "projectDetail.importDialog.documentNamePlaceholder"
                      )}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Phase 2: Uploading */}
        {phase === "uploading" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t("projectDetail.importDialog.uploadingProgress", {
                current: uploadingFileCount,
                total: totalFileCount,
              })}
            </p>
            <div className="space-y-3">
              {fileProgress.map((fp) => (
                <div key={fp.filename} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground truncate flex-1">
                      {fp.filename}
                    </span>
                    {fp.status === "done" && (
                      <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                    )}
                    {fp.status === "error" && (
                      <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
                    )}
                  </div>
                  <Progress value={fp.progress} className="h-2" />
                  {fp.error && (
                    <p className="text-xs text-red-500">{fp.error}</p>
                  )}
                </div>
              ))}
            </div>
            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 dark:bg-red-950 px-3 py-2 rounded">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {error}
              </div>
            )}
          </div>
        )}

        {/* Phase 3: Done — uploaded; show processing pipeline */}
        {phase === "done" && (
          <div className="space-y-4 py-2">
            <div className="text-center space-y-2">
              <CheckCircle className="h-10 w-10 text-green-500 mx-auto" />
              <p className="text-sm text-foreground">
                {t("projectDetail.importDialog.documentsCreated", {
                  count: documentsCreated,
                })}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(
                  "projectDetail.importDialog.processingHint",
                  "Recordings are now being transcribed and analyzed. You can close this dialog — processing continues in the background."
                )}
              </p>
            </div>
            {createdDocumentIds.length > 0 && (
              <div className="space-y-2">
                {createdDocumentIds.map((id) => (
                  <DocumentProcessingRow key={id} projectId={projectId} documentId={id} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </KiiDialogLayout>
  );
}
